import React, { useState, useEffect, useMemo } from 'react'
import MainLayout from '../../layout/MainLayout'
import { api } from '../../api/axios'
import {
    Users, Megaphone, BarChart3, FileText, AlertTriangle, RefreshCw,
    TrendingUp, Crown, UserCheck, Package as PackageIcon, IndianRupee, Sparkles,
    Clock,
} from 'lucide-react'
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
    CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'

const fmt = (n) => {
    if (n == null) return '—'
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`
    return n.toLocaleString()
}

const fmtMoney = (n) => `₹${fmt(n)}`

const fmtDate = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
}

const fmtRelative = (iso) => {
    if (!iso) return ''
    const diff = (Date.now() - new Date(iso).getTime()) / 1000
    if (diff < 60) return 'just now'
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return `${Math.floor(diff / 86400)}d ago`
}

const KpiCard = ({ icon: Icon, label, value, delta, deltaLabel, accent = 'gray', loading }) => {
    const accents = {
        gray: 'from-gray-700 to-gray-900',
        blue: 'from-blue-500 to-blue-700',
        green: 'from-emerald-500 to-emerald-700',
        purple: 'from-purple-500 to-purple-700',
        orange: 'from-orange-500 to-orange-600',
        rose: 'from-rose-500 to-rose-700',
        amber: 'from-amber-500 to-amber-600',
    }
    return (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
                <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${accents[accent]} text-white flex items-center justify-center shadow-sm`}>
                    <Icon size={18} />
                </div>
                {delta != null && (
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <TrendingUp size={12} />
                        {delta}
                    </span>
                )}
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</div>
            <div className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
                {loading ? <span className="text-gray-300">—</span> : value}
            </div>
            {deltaLabel && !loading && (
                <div className="text-xs text-gray-400 mt-1">{deltaLabel}</div>
            )}
        </div>
    )
}

const ChartCard = ({ title, subtitle, action, children, height = 280 }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-4">
            <div>
                <div className="text-sm font-semibold text-gray-900">{title}</div>
                {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
            </div>
            {action}
        </div>
        <div style={{ width: '100%', height }}>{children}</div>
    </div>
)

const DONUT_COLORS = ['#94a3b8', '#f59e0b', '#8b5cf6']

export default function Home() {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [lastUpdated, setLastUpdated] = useState(null)

    useEffect(() => {
        loadDashboard()
    }, [])

    const loadDashboard = async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await api.get('/counts/dashboard')
            setData(res.data)
            setLastUpdated(new Date())
        } catch (err) {
            console.log(err)
            const status = err?.response?.status
            if (status === 429) {
                setError('Rate limited (429) — too many requests from this IP. Wait ~15 min or restart the server.')
            } else if (status) {
                setError(`Failed to load dashboard (HTTP ${status}). ${err?.response?.data?.message || ''}`)
            } else {
                setError(`Failed to load dashboard: ${err?.message || 'unknown error'}`)
            }
        } finally {
            setLoading(false)
        }
    }

    const t = data?.totals || {}

    const premiumPct = useMemo(() => {
        if (!t.totalUsers) return 0
        return Math.round((t.premiumUsers / t.totalUsers) * 100)
    }, [t.premiumUsers, t.totalUsers])

    const activePct = useMemo(() => {
        if (!t.totalUsers) return 0
        return Math.round((t.activeUsers / t.totalUsers) * 100)
    }, [t.activeUsers, t.totalUsers])

    return (
        <MainLayout
            title="Dashboard"
            subtitle="Real-time overview of platform activity"
            Buttons={
                <div className="flex items-center gap-2">
                    {lastUpdated && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock size={12} />
                            Updated {fmtRelative(lastUpdated)}
                        </span>
                    )}
                    <button
                        onClick={loadDashboard}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white rounded-lg text-sm transition"
                    >
                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>
            }
        >
            {error && (
                <div className="mb-4 flex items-center justify-between gap-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={16} />
                        <span>{error}</span>
                    </div>
                    <button
                        onClick={loadDashboard}
                        className="flex items-center gap-1 px-2 py-1 rounded hover:bg-red-100 text-red-700"
                    >
                        <RefreshCw size={14} /> Retry
                    </button>
                </div>
            )}

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                <KpiCard
                    icon={Users} label="Total Users" value={fmt(t.totalUsers)}
                    delta={t.usersToday ? `+${t.usersToday}` : null} deltaLabel="today"
                    accent="blue" loading={loading}
                />
                <KpiCard
                    icon={UserCheck} label="Active" value={fmt(t.activeUsers)}
                    deltaLabel={`${activePct}% of total`}
                    accent="green" loading={loading}
                />
                <KpiCard
                    icon={Crown} label="Premium" value={fmt(t.premiumUsers)}
                    deltaLabel={`${premiumPct}% of total`}
                    accent="amber" loading={loading}
                />
                <KpiCard
                    icon={FileText} label="Total Posts" value={fmt(t.totalPosts)}
                    delta={t.postsToday ? `+${t.postsToday}` : null} deltaLabel="today"
                    accent="purple" loading={loading}
                />
                <KpiCard
                    icon={BarChart3} label="Total Polls" value={fmt(t.totalPolls)}
                    accent="rose" loading={loading}
                />
                <KpiCard
                    icon={Megaphone} label="Total Ads" value={fmt(t.totalAds)}
                    accent="orange" loading={loading}
                />
            </div>

            {/* Secondary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <KpiCard
                    icon={IndianRupee} label="Revenue (30d)" value={fmtMoney(t.revenue30 || 0)}
                    deltaLabel="paid payments"
                    accent="green" loading={loading}
                />
                <KpiCard
                    icon={PackageIcon} label="Active Packages" value={fmt(t.totalPackages)}
                    accent="purple" loading={loading}
                />
                <KpiCard
                    icon={Sparkles} label="Trial Users" value={fmt(t.trialUsers)}
                    accent="blue" loading={loading}
                />
                <KpiCard
                    icon={Users} label="Free Users" value={fmt(t.freeUsers)}
                    accent="gray" loading={loading}
                />
            </div>

            {/* Charts row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2">
                    <ChartCard title="User Growth" subtitle="New signups over the last 30 days">
                        {loading || !data ? (
                            <div className="h-full flex items-center justify-center text-gray-300">Loading…</div>
                        ) : (
                            <ResponsiveContainer>
                                <AreaChart data={data.userGrowth} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                                            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                    <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                                        labelFormatter={fmtDate}
                                    />
                                    <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} fill="url(#userGrad)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </div>
                <div>
                    <ChartCard title="User Type" subtitle="Distribution across plans">
                        {loading || !data ? (
                            <div className="h-full flex items-center justify-center text-gray-300">Loading…</div>
                        ) : (
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={data.userTypeDistribution}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%" cy="50%"
                                        innerRadius={50} outerRadius={85}
                                        paddingAngle={2}
                                    >
                                        {data.userTypeDistribution.map((_, i) => (
                                            <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </div>
            </div>

            {/* Charts row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2">
                    <ChartCard title="Posts Activity" subtitle="Posts created per day, last 30 days">
                        {loading || !data ? (
                            <div className="h-full flex items-center justify-center text-gray-300">Loading…</div>
                        ) : (
                            <ResponsiveContainer>
                                <BarChart data={data.postsTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                    <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                                        labelFormatter={fmtDate}
                                    />
                                    <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </div>
                <div>
                    <ChartCard title="Top Packages" subtitle="By active subscribers">
                        {loading || !data ? (
                            <div className="h-full flex items-center justify-center text-gray-300">Loading…</div>
                        ) : data.topPackages.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-gray-400 text-sm">No subscribers yet</div>
                        ) : (
                            <ResponsiveContainer>
                                <BarChart data={data.topPackages} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} width={90} />
                                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }} />
                                    <Bar dataKey="subscribers" fill="#10b981" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </div>
            </div>

            {/* Recent signups */}
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-sm font-semibold text-gray-900">Recent Signups</div>
                        <div className="text-xs text-gray-500 mt-0.5">5 most recent users</div>
                    </div>
                </div>
                {loading || !data ? (
                    <div className="text-gray-300 text-sm">Loading…</div>
                ) : data.recentSignups.length === 0 ? (
                    <div className="text-gray-400 text-sm">No users yet</div>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {data.recentSignups.map(u => (
                            <li key={u._id} className="py-3 flex items-center gap-3">
                                {u.picture ? (
                                    <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                                ) : (
                                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white text-sm font-medium">
                                        {(u.name || u.username || '?').charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 text-sm truncate">{u.name || u.username || 'Unnamed'}</div>
                                    <div className="text-xs text-gray-500 truncate">{u.email || '—'}</div>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    u.userType === 'premium' ? 'bg-amber-100 text-amber-700' :
                                    u.userType === 'trial' ? 'bg-purple-100 text-purple-700' :
                                    'bg-gray-100 text-gray-600'
                                }`}>
                                    {u.userType || 'free'}
                                </span>
                                <span className="text-xs text-gray-400 w-16 text-right">{fmtRelative(u.createdAt)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </MainLayout>
    )
}
